import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import axios from 'axios';

function IPScanner() {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleScan = async () => {
    if (!ip) {
      setError('Please enter an IP address');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Use a public ping service API
      const response = await axios.get(`https://api.ping.pe/ping?host=${ip}`);
      setResult({
        target: ip,
        alive: response.data.status === 'success',
        time: response.data.time || 'N/A'
      });
    } catch (err) {
      // Fallback - just show IP info since ping APIs are limited
      try {
        const ipInfo = await axios.get(`https://ipapi.co/${ip}/json/`);
        setResult({
          target: ip,
          alive: true,
          time: 'N/A',
          info: `${ipInfo.data.city}, ${ipInfo.data.country_name}`
        });
      } catch {
        setError('Unable to reach the target IP');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        IP Scanner
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="IP Address"
              variant="outlined"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleScan}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Scan IP'}
            </Button>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Card sx={{ 
                mt: 2, 
                p: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: result.alive ? 'success.main' : 'error.main',
                borderRadius: 2
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Scan Results
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body1">
                    <strong>Target:</strong> {result.target}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: result.alive ? 'success.main' : 'error.main',
                    fontWeight: 500
                  }}>
                    <strong>Status:</strong> {result.alive ? 'Alive' : 'Dead'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Response Time:</strong> {result.time}ms
                  </Typography>
                  {result.info && (
                    <Typography variant="body1">
                      <strong>Location:</strong> {result.info}
                    </Typography>
                  )}
                </Box>
              </Card>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default IPScanner;
